import React, {CSSProperties, useEffect, useRef} from 'react'
import type {Feature, GeoJsonObject, Geometry} from 'geojson'
import togeojson from 'togeojson'
import fileLayer from 'leaflet-filelayer'

import L from './leaflet.js'
import {baseMapOptions, createTileLayer, defaultCenter, defaultZoom, fitBoundsSafe} from './setup.js'
import {setupDrawing, toZoneGeoJSON, zoneStyle} from './geoman.js'
import {validPosition} from './geometry.js'
import useLeafletMap from './hooks/useLeafletMap.js'
import useLayerGroup from './hooks/useLayerGroup.js'
import useLatest from './hooks/useLatest.js'
import MapControls from './controls/MapControls.js'
import MapControlButton from './controls/MapControlButton.js'
import ZoneDrawingControls from './controls/ZoneDrawingControls.js'
import MapAttribution from './controls/MapAttribution.js'
import {mapContainerStyle, mapElementStyle} from './styles.js'

// Adds L.FileLayer.fileLoader, which reads GPX, KML and GeoJSON files in the browser. Its own button (a Leaflet
// control) isn't used: our import button opens a file input and passes the files to the loader.
fileLayer(null, L, togeojson)

interface FileLoader extends L.Evented {
    /** Fires data:loaded with each file's features as a GeoJSON layer, or data:error */
    loadMultiple: (files: FileList | File[]) => void
}

// Not in @types/leaflet
const createFileLoader = (L as unknown as {FileLayer: {fileLoader: (map: L.Map, options: object) => FileLoader}}).FileLayer.fileLoader

// The files the loader reads, as leaflet-filelayer's own file input accepted them
const importFormats = '.gpx,.kml,.json,.geojson'

export interface DeliveryZone {
    id: string
    name?: string
    color?: string
    geoJSON?: object
    visible?: boolean
}

export interface ImportedArea {
    geoJSON: object
    /** The name in the file, if any */
    name?: string
}

export interface DeliveryZonesMapProps {
    /** Where the map starts when there are no visible zones (the company address) */
    center?: {lat?: number, lng?: number}
    /** The zones, only the visible ones are drawn. Redrawn when the array changes identity */
    zones?: DeliveryZone[]
    /** A zone is drawn (polygon or circle) */
    onDraw: (geoJSON: object) => void
    /** A file is imported; skipped is the number of lines and points in it, which can't be zones */
    onImport: (areas: ImportedArea[], skipped: number) => void
    onImportError: () => void
    /** A zone's area is edited or cut */
    onChangeArea: (id: string, geoJSON: object) => void
    /** Place and size of the map in the layout */
    style?: CSSProperties
}

interface FileLoadedEvent extends L.LeafletEvent {
    layer: L.GeoJSON
}

// A zone is an area: lines (e.g. a GPX track) and points can't be delivery zones
function isArea(geometry?: Geometry | null): boolean {
    return ['Polygon', 'MultiPolygon'].includes(geometry?.type) ||
        (geometry?.type === 'GeometryCollection' && geometry.geometries?.length > 0 && geometry.geometries.every(isArea))
}

const createFeatureGroup = () => L.featureGroup()

/**
 * The map of the delivery zones settings: draws the visible zones and has the tools to draw, edit and cut zones
 * (geoman, with our own toolbar buttons) and to import them from a file (a button or dropped on the map). The map only reports what the user did, the caller changes the zones.
 */
export default function DeliveryZonesMap({center, zones = [], onDraw, onImport, onImportError, onChangeArea, style}: DeliveryZonesMapProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const loaderRef = useRef<FileLoader | null>(null)
    const latest = useLatest({onDraw, onImport, onImportError, onChangeArea})

    const map = useLeafletMap(containerRef, baseMapOptions, (map) => {
        map.setView(validPosition(center) || defaultCenter, defaultZoom)
        createTileLayer().addTo(map)
        setupDrawing(map)

        // The drawn shape isn't kept: the caller adds the zone, which comes back in zones
        map.on('pm:create', ({shape, layer}) => {
            layer.remove()
            latest.current.onDraw(toZoneGeoJSON(shape, layer))
        })

        const loader = createFileLoader(map, {addToMap: false, fileSizeLimit: 500000})
        loaderRef.current = loader

        loader.on('data:loaded', (event: FileLoadedEvent) => {
            const areas: ImportedArea[] = []
            let skipped = 0

            for (const layer of event.layer.getLayers() as Array<L.Layer & {feature?: Feature, toGeoJSON?: () => Feature}>) {
                if (!layer?.feature?.properties || typeof layer.toGeoJSON !== 'function') {
                    continue
                }

                const geoJSON = layer.toGeoJSON()

                if (isArea(geoJSON.geometry)) {
                    areas.push({geoJSON, name: layer.feature.properties.name || undefined})
                } else {
                    skipped++
                }
            }

            latest.current.onImport(areas, skipped)
            // Shows what was imported, as the import control did
            fitBoundsSafe(map, event.layer.getBounds())
        })

        loader.on('data:error', () => latest.current.onImportError())

        // Files dropped on the map are imported as well
        const container = map.getContainer()
        const onDragOver = (event: DragEvent) => event.preventDefault()
        const onDrop = (event: DragEvent) => {
            event.preventDefault()

            if (event.dataTransfer?.files.length) {
                loader.loadMultiple(event.dataTransfer.files)
            }
        }

        container.addEventListener('dragover', onDragOver)
        container.addEventListener('drop', onDrop)

        return () => {
            container.removeEventListener('dragover', onDragOver)
            container.removeEventListener('drop', onDrop)
            loaderRef.current = null
        }
    })

    const group = useLayerGroup(map, createFeatureGroup)
    const fitted = useRef(false)

    useEffect(() => {
        if (!map || !group) {
            return
        }

        group.clearLayers()

        for (const zone of zones) {
            if (zone.visible && zone.geoJSON) {
                addZone(group, zone, (id, geoJSON) => latest.current.onChangeArea(id, geoJSON))
            }
        }

        // Once, when the map opens; afterwards the user's view stays
        if (!fitted.current) {
            fitted.current = true
            fitBoundsSafe(map, group.getBounds())
        }
    }, [map, group, zones])

    return (
        <div style={{...mapContainerStyle, ...style}}>
            <div ref={containerRef} style={mapElementStyle} />

            <MapControls map={map}>
                <ZoneDrawingControls map={map} tools={['Polygon', 'Circle', 'Edit', 'Cut']}>
                    <MapControlButton
                        icon='mdi mdi-file-import'
                        label='Bestand importeren (GPX, KML, GeoJSON)'
                        disabled={!map}
                        onClick={() => fileInputRef.current?.click()}
                    />
                </ZoneDrawingControls>
            </MapControls>

            <input
                ref={fileInputRef}
                type='file'
                multiple
                accept={importFormats}
                hidden
                onChange={(event) => {
                    const input = event.currentTarget

                    if (input.files?.length) {
                        loaderRef.current?.loadMultiple(input.files)
                    }

                    // The same file can be imported again
                    input.value = ''
                }}
            />

            <MapAttribution />
        </div>
    )
}

/** Draws a zone's features in the group, editing or cutting one reports the zone's new area */
function addZone(group: L.FeatureGroup, zone: DeliveryZone, onChangeArea: (id: string, geoJSON: object) => void) {
    try {
        L.geoJSON(zone.geoJSON as GeoJsonObject, {
            style: () => zoneStyle(zone.color),
            onEachFeature: (feature, layer: L.Polygon) => {
                // Cutting fires an edit of the cut layer as well, which would store its old area over the new one
                let cut = false

                group.addLayer(layer)

                layer.on('pm:edit', () => {
                    if (!cut) {
                        onChangeArea(zone.id, layer.toGeoJSON())
                    }
                })

                layer.on('pm:cut', (event) => {
                    cut = true

                    // Geoman adds the result to the map itself; in the group it is replaced when the zones are redrawn
                    group.addLayer(event.layer)
                    onChangeArea(zone.id, (event.layer as L.Polygon).toGeoJSON())
                })
            }
        })
    } catch (error) {
        // An invalid zone is left out instead of breaking the map
        console.error(error)
    }
}
